import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import {
  Route, Plus, Save, Trash2, X, Edit2, Eye, EyeOff, RefreshCw, AlertCircle, CheckCircle,
  Clock, Filter, Send, Tag, MessageCircle, ChevronDown, ChevronRight, Users, Share2, Gift
} from 'lucide-react';

interface Workflow {
  id: number;
  key: string;
  name: string;
  description?: string;
  triggerType: 'event' | 'schedule';
  triggerConfig: Record<string, unknown>;
  targetType: 'customer' | 'referrer';
  isActive: number;
  maxRunsPerCustomer: number;
  steps?: WorkflowStep[];
}

interface WorkflowStep {
  id: number;
  workflowId: number;
  stepOrder: number;
  stepType: 'condition' | 'delay' | 'action';
  config: Record<string, unknown>;
  isActive: number;
}

interface AutoTagCondition {
  field: string;
  operator: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'in';
  value: string | number | boolean | string[];
}

interface CMSEntry {
  key: string;
  label: string;
  category: string;
}

interface GiftCatalogItem {
  id: number;
  key: string;
  name: string;
  description: string;
  isActive: number;
}

const CUSTOMER_FIELDS: { value: string; label: string; type: 'number' | 'text' | 'boolean' | 'tag' }[] = [
  { value: 'orderCount', label: '訂單數量', type: 'number' },
  { value: 'totalSpent', label: '累計消費（HKD）', type: 'number' },
  { value: 'daysSinceLastOrder', label: '距離最後下單天數', type: 'number' },
  { value: 'lastOrderAt', label: '最後下單時間（Unix）', type: 'number' },
  { value: 'source', label: '來源', type: 'text' },
  { value: 'isUnsubscribed', label: '已拒收', type: 'boolean' },
  { value: 'hasManualTag', label: '擁有手動標籤', type: 'tag' },
  { value: 'hasReferrer', label: '經推薦人介紹', type: 'boolean' },
];

const REFERRER_FIELDS: { value: string; label: string; type: 'number' | 'text' | 'boolean' | 'tag' }[] = [
  { value: 'referralCount', label: '推薦單數', type: 'number' },
  { value: 'totalCommission', label: '累計佣金（分）', type: 'number' },
  { value: 'paidCommission', label: '已支付佣金（分）', type: 'number' },
  { value: 'unpaidCommission', label: '未支付佣金（分）', type: 'number' },
  { value: 'orderCount', label: '個人訂單數量', type: 'number' },
  { value: 'totalSpent', label: '個人累計消費（HKD）', type: 'number' },
  { value: 'totalPageViews', label: '累積分享連結瀏覽量', type: 'number' },
  { value: 'daysSinceJoined', label: '加入天數', type: 'number' },
  { value: 'status', label: '狀態', type: 'text' },
  { value: 'isUnsubscribed', label: '已拒收', type: 'boolean' },
  { value: 'hasManualTag', label: '擁有手動標籤', type: 'tag' },
];

const OPERATOR_OPTIONS: { value: AutoTagCondition['operator']; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'ne', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'contains', label: '包含' },
  { value: 'in', label: '在列表' },
];

const ACTION_FIELDS = [
  { value: 'send_whatsapp', label: '發送 WhatsApp 模板', icon: MessageCircle },
  { value: 'add_tag', label: '加入手動標籤', icon: Tag },
  { value: 'remove_tag', label: '移除手動標籤', icon: Tag },
  { value: 'add_gift', label: '贈送隨單禮品', icon: Gift },
];

const TAG_COLORS = ['blue', 'purple', 'yellow', 'amber', 'green', 'red', 'gray'];

function formatCondition(cond: AutoTagCondition, fieldOptions: typeof CUSTOMER_FIELDS): string {
  const field = fieldOptions.find((f) => f.value === cond.field)?.label || cond.field;
  const op = OPERATOR_OPTIONS.find((o) => o.value === cond.operator)?.label || cond.operator;
  let value = Array.isArray(cond.value) ? cond.value.join(', ') : String(cond.value);
  if (cond.field === 'totalSpent' && typeof cond.value === 'number') value = `HK$${(cond.value / 100).toFixed(0)}`;
  return `${field} ${op} ${value}`;
}

function formatStep(step: WorkflowStep, fieldOptions: typeof CUSTOMER_FIELDS): string {
  if (step.stepType === 'condition') {
    const conditions = (step.config.conditions || []) as AutoTagCondition[];
    if (!conditions.length) return '無條件（永遠通過）';
    return conditions.map((c) => formatCondition(c, fieldOptions)).join(' 且 ');
  }
  if (step.stepType === 'delay') {
    const parts: string[] = [];
    if (step.config.days) parts.push(`${step.config.days} 天`);
    if (step.config.hours) parts.push(`${step.config.hours} 小時`);
    if (step.config.minutes) parts.push(`${step.config.minutes} 分鐘`);
    return parts.length ? `等待 ${parts.join(' ')}` : '無延遲';
  }
  if (step.stepType === 'action') {
    const type = String(step.config.type || '');
    if (type === 'send_whatsapp') return `發送 WhatsApp：${step.config.templateKey || '未選擇'}`;
    if (type === 'add_tag') return `加標籤：${step.config.tag || '未命名'}`;
    if (type === 'remove_tag') return `移除標籤：${step.config.tag || '未命名'}`;
    if (type === 'add_gift') return `贈送禮品：${step.config.giftKey || '未選擇'}`;
    return type;
  }
  return '';
}

function getStepIcon(stepType: string) {
  if (stepType === 'condition') return Filter;
  if (stepType === 'delay') return Clock;
  return Send;
}

export default function AdminCustomerJourneys() {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null);
  const [workflowForm, setWorkflowForm] = useState<Partial<Workflow>>({
    key: '', name: '', description: '', triggerType: 'event', triggerConfig: { event: 'order.completed' }, targetType: 'customer', isActive: 1, maxRunsPerCustomer: 0,
  });

  const [stepModalOpen, setStepModalOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<WorkflowStep | null>(null);
  const [currentWorkflowId, setCurrentWorkflowId] = useState<number>(0);
  const [stepForm, setStepForm] = useState<Partial<WorkflowStep>>({
    stepOrder: 0, stepType: 'condition', config: { conditions: [] }, isActive: 1,
  });

  const [whatsappTemplates, setWhatsappTemplates] = useState<CMSEntry[]>([]);
  const [giftCatalog, setGiftCatalog] = useState<GiftCatalogItem[]>([]);
  const [activeTab, setActiveTab] = useState<'customer' | 'referrer'>('customer');
  const [currentWorkflowTargetType, setCurrentWorkflowTargetType] = useState<'customer' | 'referrer'>('customer');
  const fieldOptions = currentWorkflowTargetType === 'referrer' ? REFERRER_FIELDS : CUSTOMER_FIELDS;

  const fetchWorkflows = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/public/admin/workflows');
      if (!res.ok) throw new Error('載入失敗');
      const data = await res.json();
      setWorkflows(Array.isArray(data) ? data : data.data || []);
    } catch (e: any) {
      setError(e.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await apiFetch('/api/public/admin/cms');
      if (!res.ok) return;
      const data = await res.json();
      const list = (Array.isArray(data) ? data : data.data || []) as CMSEntry[];
      setWhatsappTemplates(list.filter((e) => e.key.startsWith('whatsapp_')));
    } catch (e) { console.error(e); }
  };

  const fetchGiftCatalog = async () => {
    try {
      const res = await apiFetch('/api/public/admin/gift-catalog');
      if (!res.ok) return;
      const data = await res.json();
      setGiftCatalog((Array.isArray(data) ? data : data.data || []) as GiftCatalogItem[]);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchWorkflows();
    fetchTemplates();
    fetchGiftCatalog();
  }, [navigate]);

  const showToast = (msg: string, isError = false) => {
    if (isError) { setError(msg); setTimeout(() => setError(null), 4000); }
    else { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); }
  };

  const openCreateWorkflow = () => {
    setEditingWorkflow(null);
    const defaultEvent = activeTab === 'referrer' ? 'referrer.created' : 'order.completed';
    setWorkflowForm({ key: '', name: '', description: '', triggerType: 'event', triggerConfig: { event: defaultEvent }, targetType: activeTab, isActive: 1, maxRunsPerCustomer: 0 });
    setCurrentWorkflowTargetType(activeTab);
    setWorkflowModalOpen(true);
  };

  const openEditWorkflow = (wf: Workflow) => {
    setEditingWorkflow(wf);
    setWorkflowForm({ ...wf });
    setCurrentWorkflowTargetType(wf.targetType);
    setWorkflowModalOpen(true);
  };

  const saveWorkflow = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workflowForm.key?.trim() || !workflowForm.name?.trim()) {
      showToast('請填寫 Key 與名稱', true); return;
    }
    if (!/^[a-z0-9_-]+$/.test(workflowForm.key.trim())) {
      showToast('Key 只可使用小寫英文、數字、底線或橫線', true); return;
    }
    try {
      const url = editingWorkflow
        ? `/api/public/admin/workflows/${encodeURIComponent(editingWorkflow.key)}`
        : '/api/public/admin/workflows';
      const res = await apiFetch(url, {
        method: editingWorkflow ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(workflowForm),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      showToast(editingWorkflow ? '已更新旅程' : '已建立旅程');
      setWorkflowModalOpen(false);
      fetchWorkflows();
    } catch (err: any) { showToast(err.message || '儲存失敗', true); }
  };

  const deleteWorkflow = async (wf: Workflow) => {
    if (!confirm(`確定刪除「${wf.name}」？`)) return;
    try {
      const res = await apiFetch(`/api/public/admin/workflows/${encodeURIComponent(wf.key)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('刪除失敗');
      showToast('已刪除'); fetchWorkflows();
    } catch (err: any) { showToast(err.message || '刪除失敗', true); }
  };

  const toggleWorkflowActive = async (wf: Workflow) => {
    try {
      const res = await apiFetch(`/api/public/admin/workflows/${encodeURIComponent(wf.key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !wf.isActive }),
      });
      if (!res.ok) throw new Error('更新失敗');
      fetchWorkflows();
    } catch (err: any) { showToast(err.message || '更新失敗', true); }
  };

  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openCreateStep = (wf: Workflow) => {
    setEditingStep(null);
    setCurrentWorkflowId(wf.id);
    setCurrentWorkflowTargetType(wf.targetType);
    const maxOrder = (wf.steps || []).reduce((m, s) => Math.max(m, s.stepOrder), -1);
    const defaultField = wf.targetType === 'referrer' ? 'referralCount' : 'orderCount';
    setStepForm({ stepOrder: maxOrder + 1, stepType: 'condition', config: { conditions: [{ field: defaultField, operator: 'eq', value: 1 }] }, isActive: 1 });
    setStepModalOpen(true);
  };

  const openEditStep = (wf: Workflow, step: WorkflowStep) => {
    setEditingStep(step);
    setCurrentWorkflowId(wf.id);
    setCurrentWorkflowTargetType(wf.targetType);
    setStepForm({ ...step, config: JSON.parse(JSON.stringify(step.config)) });
    setStepModalOpen(true);
  };

  const normalizeConditionValue = (cond: AutoTagCondition): AutoTagCondition['value'] => {
    if (cond.field === 'totalSpent') {
      const n = Number(cond.value);
      return Number.isNaN(n) ? 0 : Math.round(n * 100);
    }
    if (cond.field === 'isUnsubscribed') return !!cond.value;
    if (cond.field === 'hasManualTag') return String(cond.value || '').trim();
    const fieldType = fieldOptions.find((f) => f.value === cond.field)?.type;
    if (fieldType === 'number') { const n = Number(cond.value); return Number.isNaN(n) ? 0 : n; }
    return String(cond.value || '');
  };

  const saveStep = async (e: React.FormEvent) => {
    e.preventDefault();
    const wf = workflows.find((w) => w.id === currentWorkflowId);
    if (!wf) return;

    let config = { ...stepForm.config };
    if (stepForm.stepType === 'condition') {
      const conditions = ((config.conditions || []) as AutoTagCondition[]).map((c) => ({ ...c, value: normalizeConditionValue(c) }));
      config = { conditions };
    }
    if (stepForm.stepType === 'delay') {
      config = {
        days: Number(config.days || 0),
        hours: Number(config.hours || 0),
        minutes: Number(config.minutes || 0),
      };
    }
    if (stepForm.stepType === 'action') {
      config = { ...config };
    }

    try {
      const url = editingStep
        ? `/api/public/admin/workflow-steps/${editingStep.id}`
        : `/api/public/admin/workflows/${encodeURIComponent(wf.key)}/steps`;
      const res = await apiFetch(url, {
        method: editingStep ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stepOrder: stepForm.stepOrder,
          stepType: stepForm.stepType,
          config,
          isActive: stepForm.isActive,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      showToast(editingStep ? '已更新步驟' : '已建立步驟');
      setStepModalOpen(false);
      fetchWorkflows();
    } catch (err: any) { showToast(err.message || '儲存失敗', true); }
  };

  const deleteStep = async (wf: Workflow, step: WorkflowStep) => {
    if (!confirm('確定刪除此步驟？')) return;
    try {
      const res = await apiFetch(`/api/public/admin/workflow-steps/${step.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('刪除失敗');
      showToast('已刪除'); fetchWorkflows();
    } catch (err: any) { showToast(err.message || '刪除失敗', true); }
  };

  const handleConditionChange = (index: number, patch: Partial<AutoTagCondition>) => {
    setStepForm((prev) => {
      const conditions = [...((prev.config?.conditions || []) as AutoTagCondition[])];
      conditions[index] = { ...conditions[index], ...patch };
      if (patch.field) {
        const fieldType = fieldOptions.find((f) => f.value === conditions[index].field)?.type;
        if (fieldType === 'boolean') conditions[index].value = false;
        else if (fieldType === 'tag') conditions[index].value = '';
        else conditions[index].value = 0;
      }
      return { ...prev, config: { ...prev.config, conditions } };
    });
  };

  const addCondition = () => {
    const defaultField = currentWorkflowTargetType === 'referrer' ? 'referralCount' : 'orderCount';
    setStepForm((prev) => ({
      ...prev,
      config: { ...prev.config, conditions: [...((prev.config?.conditions || []) as AutoTagCondition[]), { field: defaultField, operator: 'eq', value: 1 }] },
    }));
  };

  const removeCondition = (index: number) => {
    setStepForm((prev) => ({
      ...prev,
      config: { ...prev.config, conditions: ((prev.config?.conditions || []) as AutoTagCondition[]).filter((_, i) => i !== index) },
    }));
  };

  const displayConditionValue = (cond: AutoTagCondition): string | number | boolean => {
    if (cond.field === 'totalSpent' && typeof cond.value === 'number') return cond.value / 100;
    return cond.value;
  };

  return (
    <AdminLayout currentPage="customer-journeys">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Route className="w-6 h-6 text-red-600" />
          客戶路徑
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          設計自動化客戶旅程：觸發 → 條件 → 延遲 → 動作，推動復購與夥伴轉化。
        </p>
      </div>

      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('customer')}
            className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'customer'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <Users size={16} /> 客戶旅程
          </button>
          <button
            onClick={() => setActiveTab('referrer')}
            className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'referrer'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <Share2 size={16} /> 推薦人旅程
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span className="flex items-center gap-2"><AlertCircle size={16} /> {error}</span>
          <button onClick={() => setError(null)}><X size={18} /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-2"><CheckCircle size={16} /> {success}</span>
          <button onClick={() => setSuccess(null)}><X size={18} /></button>
        </div>
      )}

      <div className="mb-4 flex justify-end gap-2">
        <button onClick={fetchWorkflows} className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
          <RefreshCw size={16} /> 重新整理
        </button>
        <button onClick={openCreateWorkflow} className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors">
          <Plus size={16} /> 新增旅程
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">載入中...</div>
      ) : workflows.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          尚未建立客戶旅程
        </div>
      ) : (
        <div className="space-y-4">
          {workflows.filter((wf) => wf.targetType === activeTab).map((wf) => (
            <div key={wf.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-900 dark:text-white">{wf.name}</span>
                    <span className="text-xs text-gray-400 font-mono">{wf.key}</span>
                    {wf.isActive ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">啟用</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">停用</span>
                    )}
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                      {wf.triggerType === 'event' ? '事件觸發' : '排程觸發'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    觸發：{(wf.triggerConfig as any).event || wf.triggerType} · 對象：{wf.targetType === 'customer' ? '客戶' : '夥伴'}
                  </p>
                  {wf.description && <p className="text-xs text-gray-400 mt-1">{wf.description}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => toggleExpand(wf.id)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400">
                    {expanded.has(wf.id) ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  <button onClick={() => toggleWorkflowActive(wf)} className={`${wf.isActive ? 'text-green-600' : 'text-gray-400'} hover:opacity-70`} title={wf.isActive ? '停用' : '啟用'}>
                    {wf.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                  <button onClick={() => openEditWorkflow(wf)} className="text-blue-600 hover:text-blue-700" title="編輯"><Edit2 size={18} /></button>
                  <button onClick={() => deleteWorkflow(wf)} className="text-red-600 hover:text-red-700" title="刪除"><Trash2 size={18} /></button>
                </div>
              </div>

              {expanded.has(wf.id) && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50/50 dark:bg-gray-900/30">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200">步驟</h3>
                    <button onClick={() => openCreateStep(wf)} className="text-xs flex items-center gap-1 text-red-600 hover:text-red-700 font-medium">
                      <Plus size={14} /> 新增步驟
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(wf.steps || []).length === 0 ? (
                      <p className="text-sm text-gray-400">尚未建立步驟</p>
                    ) : (
                      (wf.steps || []).sort((a, b) => a.stepOrder - b.stepOrder).map((step, idx) => {
                        const Icon = getStepIcon(step.stepType);
                        return (
                          <div key={step.id} className="flex items-start gap-3 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="flex flex-col items-center">
                              <span className="w-6 h-6 rounded-full bg-red-100 text-red-700 text-xs flex items-center justify-center font-bold">{idx + 1}</span>
                              {idx < (wf.steps || []).length - 1 && <div className="w-px h-full bg-gray-200 dark:bg-gray-700 my-1" />}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Icon size={14} className="text-gray-400" />
                                <span className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                  {step.stepType === 'condition' ? '條件' : step.stepType === 'delay' ? '延遲' : '動作'}
                                </span>
                              </div>
                              <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">{formatStep(step, wf.targetType === 'referrer' ? REFERRER_FIELDS : CUSTOMER_FIELDS)}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <button onClick={() => openEditStep(wf, step)} className="text-blue-600 hover:text-blue-700"><Edit2 size={16} /></button>
                              <button onClick={() => deleteStep(wf, step)} className="text-red-600 hover:text-red-700"><Trash2 size={16} /></button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Workflow Modal */}
      {workflowModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={saveWorkflow} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{editingWorkflow ? '編輯旅程' : '新增旅程'}</h2>
                <button type="button" onClick={() => setWorkflowModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Key *</label>
                    <input disabled={!!editingWorkflow} value={workflowForm.key || ''} onChange={(e) => setWorkflowForm({ ...workflowForm, key: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white disabled:bg-gray-100" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">名稱 *</label>
                    <input value={workflowForm.name || ''} onChange={(e) => setWorkflowForm({ ...workflowForm, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">說明</label>
                  <input value={workflowForm.description || ''} onChange={(e) => setWorkflowForm({ ...workflowForm, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">觸發類型</label>
                    <select value={workflowForm.triggerType} onChange={(e) => setWorkflowForm({ ...workflowForm, triggerType: e.target.value as any })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white">
                      <option value="event">事件觸發</option>
                      <option value="schedule">排程觸發</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">觸發事件</label>
                    <select value={((workflowForm.triggerConfig as any)?.event as string) || ''} onChange={(e) => setWorkflowForm({ ...workflowForm, triggerConfig: { ...(workflowForm.triggerConfig || {}), event: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white">
                      <option value="order.created">訂單創建</option>
                      <option value="order.payment_confirmed">付款確認</option>
                      <option value="order.completed">訂單完成</option>
                      <option value="order.pending_payment">待付款</option>
                      <option value="referrer.created">推薦人創建</option>
                      <option value="daily">每天檢查</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">對象類型</label>
                    <select value={workflowForm.targetType} onChange={(e) => setWorkflowForm({ ...workflowForm, targetType: e.target.value as any })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white">
                      <option value="customer">客戶</option>
                      <option value="referrer">夥伴</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">每客戶最多執行次數（0=無限）</label>
                    <input type="number" value={workflowForm.maxRunsPerCustomer || 0} onChange={(e) => setWorkflowForm({ ...workflowForm, maxRunsPerCustomer: Number(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white" />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={!!workflowForm.isActive} onChange={(e) => setWorkflowForm({ ...workflowForm, isActive: e.target.checked ? 1 : 0 })} className="w-4 h-4 text-red-600 rounded" />
                      啟用
                    </label>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setWorkflowModalOpen(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">取消</button>
                <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 flex items-center gap-2"><Save size={16} /> 儲存</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Step Modal */}
      {stepModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={saveStep} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{editingStep ? '編輯步驟' : '新增步驟'}</h2>
                <button type="button" onClick={() => setStepModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">步驟類型</label>
                    <select value={stepForm.stepType} onChange={(e) => setStepForm({ ...stepForm, stepType: e.target.value as any, config: e.target.value === 'condition' ? { conditions: [] } : e.target.value === 'delay' ? { days: 0, hours: 0, minutes: 0 } : { type: 'send_whatsapp' } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white">
                      <option value="condition">條件</option>
                      <option value="delay">延遲</option>
                      <option value="action">動作</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">順序</label>
                    <input type="number" value={stepForm.stepOrder || 0} onChange={(e) => setStepForm({ ...stepForm, stepOrder: Number(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white" />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={stepForm.isActive !== 0} onChange={(e) => setStepForm({ ...stepForm, isActive: e.target.checked ? 1 : 0 })} className="w-4 h-4 text-red-600 rounded" />
                      啟用
                    </label>
                  </div>
                </div>

                {stepForm.stepType === 'condition' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">條件（需全部符合）</label>
                    <div className="space-y-3">
                      {(((stepForm.config as any)?.conditions || []) as AutoTagCondition[]).map((cond, idx) => (
                        <div key={idx} className="flex flex-col md:flex-row gap-2 items-start md:items-center bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg">
                          <select value={cond.field} onChange={(e) => handleConditionChange(idx, { field: e.target.value })} className="w-full md:w-40 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white text-sm">
                            {fieldOptions.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                          </select>
                          <select value={cond.operator} onChange={(e) => handleConditionChange(idx, { operator: e.target.value as AutoTagCondition['operator'] })} className="w-full md:w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white text-sm">
                            {OPERATOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          {fieldOptions.find((f) => f.value === cond.field)?.type === 'boolean' ? (
                            <select value={cond.value ? 'true' : 'false'} onChange={(e) => handleConditionChange(idx, { value: e.target.value === 'true' })} className="w-full md:flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white text-sm">
                              <option value="true">是</option>
                              <option value="false">否</option>
                            </select>
                          ) : (
                            <input
                              type={fieldOptions.find((f) => f.value === cond.field)?.type === 'number' ? 'number' : 'text'}
                              value={displayConditionValue(cond)}
                              onChange={(e) => handleConditionChange(idx, { value: cond.field === 'totalSpent' ? Number(e.target.value) : e.target.value })}
                              className="w-full md:flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                            />
                          )}
                          <button type="button" onClick={() => removeCondition(idx)} className="text-red-600 hover:text-red-700"><X size={18} /></button>
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={addCondition} className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium">+ 增加條件</button>
                  </div>
                )}

                {stepForm.stepType === 'delay' && (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">天</label>
                      <input type="number" value={(stepForm.config as any)?.days || 0} onChange={(e) => setStepForm({ ...stepForm, config: { ...(stepForm.config || {}), days: Number(e.target.value) } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">小時</label>
                      <input type="number" value={(stepForm.config as any)?.hours || 0} onChange={(e) => setStepForm({ ...stepForm, config: { ...(stepForm.config || {}), hours: Number(e.target.value) } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">分鐘</label>
                      <input type="number" value={(stepForm.config as any)?.minutes || 0} onChange={(e) => setStepForm({ ...stepForm, config: { ...(stepForm.config || {}), minutes: Number(e.target.value) } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white" />
                    </div>
                  </div>
                )}

                {stepForm.stepType === 'action' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">動作類型</label>
                      <select value={(stepForm.config as any)?.type || 'send_whatsapp'} onChange={(e) => setStepForm({ ...stepForm, config: { type: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white">
                        {ACTION_FIELDS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                      </select>
                    </div>
                    {(stepForm.config as any)?.type === 'send_whatsapp' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">WhatsApp 模板</label>
                        <select value={(stepForm.config as any)?.templateKey || ''} onChange={(e) => setStepForm({ ...stepForm, config: { ...(stepForm.config || {}), type: 'send_whatsapp', templateKey: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white">
                          <option value="">選擇模板...</option>
                          {whatsappTemplates.map((t) => <option key={t.key} value={t.key}>{t.label || t.key}</option>)}
                        </select>
                      </div>
                    )}
                    {(stepForm.config as any)?.type === 'add_tag' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">標籤名稱</label>
                          <input value={(stepForm.config as any)?.tag || ''} onChange={(e) => setStepForm({ ...stepForm, config: { ...(stepForm.config || {}), type: 'add_tag', tag: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">顏色</label>
                          <select value={(stepForm.config as any)?.color || 'blue'} onChange={(e) => setStepForm({ ...stepForm, config: { ...(stepForm.config || {}), type: 'add_tag', color: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white">
                            {TAG_COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>
                    )}
                    {(stepForm.config as any)?.type === 'add_gift' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">禮品</label>
                        <select value={(stepForm.config as any)?.giftKey || ''} onChange={(e) => setStepForm({ ...stepForm, config: { ...(stepForm.config || {}), type: 'add_gift', giftKey: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white">
                          <option value="">選擇禮品...</option>
                          {giftCatalog.filter((g) => g.isActive).map((g) => <option key={g.key} value={g.key}>{g.name}</option>)}
                        </select>
                        {giftCatalog.length === 0 && <p className="text-xs text-red-500 mt-1">尚未建立啟用中的禮品，請先到「禮品目錄」新增。</p>}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setStepModalOpen(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">取消</button>
                <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 flex items-center gap-2"><Save size={16} /> 儲存步驟</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
